import { useEffect, useMemo, useRef } from 'react';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { useMyTournamentsQuery } from '@/services/endpoints/tournaments';
import { laravelOwnsData } from '@/lib/dataOwner';
import { deriveTournamentPrompts, saveEngagement } from '@/lib/engagement';
import {
  enqueuePrompts,
  pruneStale,
  selectInboxCooldowns,
  selectInboxItems,
} from '@/store/engagement.slice';

/**
 * Phase-1 engagement producer. Mounted once in `AppShell`. Polls the user's
 * tournaments, derives actionable prompts (register / check-in / play), and
 * feeds them into the engagement slice (which surfaces them as a nudge + inbox).
 * No-op until authenticated. Also persists the inbox + cooldowns on change.
 */
export function useEngagementEngine(): void {
  const dispatch = useAppDispatch();
  const authed = useAppSelector((s) => s.auth.bridgeAuthenticated);

  // Reminders don't need 8s freshness; a slower poll keeps network + auth-token
  // refresh pressure low (the Tournaments route still polls fast when open).
  // ⚠️ Su una build Limulo questa query NON parte, ed e' una rinuncia DICHIARATA, non una
  // svista. `myTournaments` e' una rotta del monolite: con Limulo acceso i tornei
  // dell'utente stanno altrove, quindi i promemoria d'ingaggio **non hanno una fonte** e
  // non vengono generati.
  //
  // 🔑 La rinuncia non toglie niente che funzionasse: con Limulo acceso questa query
  // rispondeva comunque a vuoto (o 503, col monolite spento), quindi `data` era gia'
  // `undefined` e nessun promemoria veniva derivato. **Cambia solo il rumore**: era un
  // poller a 30s, su OGNI schermata, che a ogni giro tirava un `exchange-token` verso un
  // backend che non e' di questa build. Misurato il 2026-09-05.
  //
  // ⇒ Quando i promemoria dovranno esistere anche su Limulo, la fonte e'
  // `getMyLimuloTournaments` (gia' cablata) e `deriveTournamentPrompts` va adattata alla
  // sua forma: e' un lavoro di prodotto, non una riga di skip.
  const { data } = useMyTournamentsQuery(
    { limit: 20 },
    { pollingInterval: 30000, refetchOnFocus: true, skip: !authed || !laravelOwnsData() },
  );

  const prompts = useMemo(
    () => (data?.data ? deriveTournamentPrompts(data.data, Date.now()) : []),
    [data],
  );

  // Only dispatch when the derived set actually changes (avoids 8s-poll churn).
  const signature = prompts.map((p) => p.id).join('|');
  const lastSig = useRef<string | null>(null);

  useEffect(() => {
    if (!authed) return;
    if (signature === lastSig.current) return;
    lastSig.current = signature;
    dispatch(enqueuePrompts(prompts));
    dispatch(pruneStale(prompts.map((p) => p.id)));
  }, [authed, signature, prompts, dispatch]);

  // Persist inbox + cooldowns whenever they change (engine OR inbox-UI actions).
  const inbox = useAppSelector(selectInboxItems);
  const cooldowns = useAppSelector(selectInboxCooldowns);
  useEffect(() => {
    saveEngagement({ inbox, cooldowns });
  }, [inbox, cooldowns]);
}
